import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, User, Eye, Check, Clock, Shield, Target, Lightbulb, MessageCircle, Video, Calendar, Award, BookOpen, Compass, Quote } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Rating } from '../components/ui/Rating';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface ConsultationPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-23276">
      {children}
    </motion.section>;
}
const reviews = [{
  name: 'Анна К.',
  avatar: 'А',
  rating: 5,
  text: 'Консультация превзошла все ожидания. Астролог очень точно описал мою ситуацию и дал конкретные рекомендации. Теперь я понимаю, почему последний год был таким сложным.',
  color: 'bg-rose-100 text-rose-700'
}, {
  name: 'Дмитрий С.',
  avatar: 'Д',
  rating: 5,
  text: 'Обратился перед сменой работы. Астролог помог увидеть благоприятный период для перемен и подсказал, на что обратить внимание. Через месяц получил отличное предложение.',
  color: 'bg-sky-100 text-sky-700'
}, {
  name: 'Елена М.',
  avatar: 'Е',
  rating: 5,
  text: 'Заказывала консультацию по отношениям. Получила глубокий анализ совместимости и практические советы. Очень помогло разобраться в ситуации с партнёром.',
  color: 'bg-amber-100 text-amber-700'
}, {
  name: 'Михаил Р.',
  avatar: 'М',
  rating: 4.5,
  text: 'Профессиональный подход, всё чётко и по делу. Особенно ценно, что астролог не просто описывает карту, а даёт конкретные рекомендации для действий.',
  color: 'bg-emerald-100 text-emerald-700'
}, {
  name: 'Ольга В.',
  avatar: 'О',
  rating: 5,
  text: 'Третий раз обращаюсь за консультацией — каждый раз получаю точный и полезный анализ. Рекомендую всем, кто хочет лучше понять себя и свои жизненные циклы.',
  color: 'bg-violet-100 text-violet-700'
}, {
  name: 'Сергей Л.',
  avatar: 'С',
  rating: 5,
  text: 'Скептически относился к астрологии, но решил попробовать. Был поражён точностью описания моего характера и жизненных ситуаций. Теперь регулярно консультируюсь.',
  color: 'bg-indigo-100 text-indigo-700'
}];
export function ConsultationPage({
  onNavigate
}: ConsultationPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBookConsultation = (tier: 'short' | 'standard' | 'extended') => {
    const tiers = {
      short: {
        name: 'Короткая консультация',
        description: 'Экспресс-разбор одного конкретного вопроса (30 минут онлайн)',
        price: 2990
      },
      standard: {
        name: 'Стандартная консультация',
        description: 'Полный разбор натальной карты с ответами на вопросы (60 минут онлайн)',
        price: 4990
      },
      extended: {
        name: 'Расширенная консультация',
        description: 'Глубокий разбор карты, транзитов и соляра (90 минут онлайн)',
        price: 7990
      }
    };
    const t = tiers[tier];
    addItem({
      id: `consultation-${tier}-${Date.now()}`,
      name: t.name,
      description: t.description,
      price: t.price,
      type: 'consultation',
      format: 'Видеозвонок',
      duration: tier === 'short' ? '30 минут' : tier === 'standard' ? '60 минут' : '90 минут',
      badges: [{
        label: 'Консультация',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-23277">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-23278">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-sky-500/12 rounded-full blur-3xl pointer-events-none" data-id="element-23279" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-23280" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23281">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Консультация'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-23282" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-23283">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-23284">
              <motion.div variants={staggerItem} className="mb-4 sm:mb-5" data-id="element-23285">
                <Badge variant="pro" className="bg-sky-500/20 text-sky-300 border-sky-500/30 px-3 py-1 text-sm" data-id="element-23286">
                  Живое общение
                </Badge>
              </motion.div>

              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-23287">
                Персональная консультация с профессиональным астрологом
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-23288">
                Получите индивидуальный разбор вашей натальной карты и ответы на
                важные жизненные вопросы от опытного эксперта.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-23289">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-sky-500/20 bg-sky-500 hover:bg-sky-600" data-id="element-23290">
                  Записаться на консультацию
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-23291" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-23292">
                {[{
                icon: <User className="w-4 h-4" data-id="element-23293" />,
                text: 'Индивидуальный разбор'
              }, {
                icon: <MessageCircle className="w-4 h-4" data-id="element-23294" />,
                text: 'Ответы на личные вопросы'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-23295" />,
                text: 'Рекомендации по решениям'
              }, {
                icon: <Clock className="w-4 h-4" data-id="element-23296" />,
                text: 'Понимание жизненных периодов'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-23297">
                    <div className="w-7 h-7 rounded-lg bg-sky-500/20 flex items-center justify-center text-sky-300 flex-shrink-0" data-id="element-23298">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — consultation concept */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-23299">
              <div className="relative w-[400px] h-[400px]" data-id="element-23300">
                {/* Video call frame */}
                <div className="absolute inset-8 bg-slate-800/80 rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden" data-id="element-23301">
                  <div className="h-8 bg-slate-700/50 flex items-center px-3 gap-1.5" data-id="element-23302">
                    <div className="w-2.5 h-2.5 rounded-full bg-rose-400/60" data-id="element-23303" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" data-id="element-23304" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" data-id="element-23305" />
                  </div>
                  <div className="p-6 flex flex-col items-center justify-center h-[calc(100%-2rem)]" data-id="element-23306">
                    {/* Astrologer avatar */}
                    <motion.div animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center mb-4 shadow-lg shadow-sky-500/20" data-id="element-23307">
                      <User className="w-10 h-10 text-white" strokeWidth={1.5} data-id="element-23308" />
                    </motion.div>
                    <div className="w-24 h-2.5 bg-slate-600 rounded mb-2" data-id="element-23309" />
                    <div className="w-16 h-2 bg-slate-700 rounded mb-4" data-id="element-23310" />
                    {/* Chat bubbles */}
                    <div className="w-full space-y-2" data-id="element-23311">
                      <motion.div initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 1,
                      duration: 0.4
                    }} className="w-3/4 h-6 bg-sky-500/20 rounded-lg rounded-bl-none" data-id="element-23312" />
                      <motion.div initial={{
                      opacity: 0,
                      x: 10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 1.5,
                      duration: 0.4
                    }} className="w-2/3 h-6 bg-slate-600/50 rounded-lg rounded-br-none ml-auto" data-id="element-23313" />
                    </div>
                  </div>
                </div>
                {/* Floating elements */}
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute top-2 right-2 w-12 h-12 bg-sky-500/10 rounded-xl flex items-center justify-center border border-sky-500/20" data-id="element-23314">
                  <Star className="w-5 h-5 text-sky-400/60" data-id="element-23315" />
                </motion.div>
                <motion.div animate={{
                y: [0, 6, 0]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5
              }} className="absolute bottom-4 left-2 w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center border border-amber-500/20" data-id="element-23316">
                  <Sparkles className="w-5 h-5 text-amber-400/60" data-id="element-23317" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. ЧТО ТАКОЕ КОНСУЛЬТАЦИЯ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23318">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23319">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-23320">
            Что такое консультация с астрологом
          </h2>
          <div className="prose prose-lg prose-slate max-w-none text-slate-600 space-y-6" data-id="element-23321">
            <p data-id="element-23322">
              Консультация — это персональная встреча с профессиональным
              астрологом, во время которой проводится глубокий анализ вашей
              натальной карты и обсуждаются важные жизненные вопросы. В отличие
              от автоматических отчётов, живая консультация позволяет задать
              вопросы, получить пояснения и обсудить именно то, что волнует вас
              прямо сейчас.
            </p>
            <p data-id="element-23323">
              Перед встречей астролог{' '}
              <strong data-id="element-23324">заранее анализирует вашу натальную карту</strong>, изучает
              текущие транзиты и готовит персональный разбор. Это значит, что
              время консультации используется максимально эффективно — вы
              получаете не общие фразы, а конкретные ответы и рекомендации.
            </p>
            <p data-id="element-23325">
              Астролог помогает понять особенности личности, разобраться в
              текущих жизненных ситуациях, увидеть перспективы развития и
              получить рекомендации для принятия важных решений. Это как
              навигатор, который помогает выбрать лучший маршрут в сложной
              ситуации.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. КАКИЕ ВОПРОСЫ МОЖНО ОБСУДИТЬ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23326">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23327">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23328">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23329">
              Какие вопросы можно обсудить
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23330">
              Консультация охватывает все ключевые сферы жизни
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-23331">
            {[{
            icon: <Heart className="w-6 h-6" data-id="element-23332" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Отношения и любовь',
            desc: 'Понимание динамики отношений, совместимость с партнёром, поиск гармонии в личной жизни.'
          }, {
            icon: <Briefcase className="w-6 h-6" data-id="element-23333" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и предназначение',
            desc: 'Выбор профессионального направления, развитие карьеры, финансовые возможности.'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-23334" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личностное развитие',
            desc: 'Понимание своих сильных сторон, внутренних задач и направлений роста.'
          }, {
            icon: <Clock className="w-6 h-6" data-id="element-23335" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Жизненные периоды',
            desc: 'Анализ текущих жизненных циклов, транзитов и предстоящих изменений.'
          }, {
            icon: <Compass className="w-6 h-6" data-id="element-23336" />,
            color: 'bg-sky-50 text-sky-600',
            title: 'Важные решения',
            desc: 'Помощь в выборе направления, определение благоприятного времени для действий.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} data-id="element-23337">
                <Card padding="lg" className="h-full border-slate-100" data-id="element-23338">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mb-4 sm:mb-5`} data-id="element-23339">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-23340">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-23341">
                    {item.desc}
                  </p>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* 4. КАК ПРОХОДИТ КОНСУЛЬТАЦИЯ */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-23342">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23343">
          <div className="text-center mb-8 sm:mb-16" data-id="element-23344">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23345">
              Как проходит консультация
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23346">
              Четыре простых шага от записи до ответов
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-8 relative" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-23347">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-px bg-slate-200 z-0" data-id="element-23348" />
            {[{
            step: '01',
            icon: <Calendar className="w-6 h-6" data-id="element-23349" />,
            title: 'Выбор времени',
            desc: 'Вы выбираете удобную дату и время для онлайн-встречи'
          }, {
            step: '02',
            icon: <BookOpen className="w-6 h-6" data-id="element-23350" />,
            title: 'Подготовка',
            desc: 'Астролог заранее анализирует вашу натальную карту и транзиты'
          }, {
            step: '03',
            icon: <Video className="w-6 h-6" data-id="element-23351" />,
            title: 'Консультация',
            desc: 'Проводится онлайн-встреча с разбором карты и ответами на вопросы'
          }, {
            step: '04',
            icon: <Lightbulb className="w-6 h-6" data-id="element-23352" />,
            title: 'Рекомендации',
            desc: 'Вы получаете подробные объяснения и практические советы'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="relative z-10 flex flex-col items-center text-center" data-id="element-23353">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-4 shadow-lg" data-id="element-23354">
                  {item.icon}
                </div>
                <div className="text-[10px] font-bold text-slate-400 tracking-[0.2em] mb-1" data-id="element-23355">
                  {item.step}
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-23356">
                  {item.title}
                </h3>
                <p className="text-xs text-slate-500 max-w-[180px]" data-id="element-23357">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* 5. ФОРМАТ КОНСУЛЬТАЦИИ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23358">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23359">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-23360">
            Формат консультации
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-23361">
            {[{
            icon: <Video className="w-5 h-5" data-id="element-23362" />,
            title: 'Онлайн-встреча',
            desc: 'Видеозвонок через Zoom или Google Meet — из любой точки мира'
          }, {
            icon: <Clock className="w-5 h-5" data-id="element-23363" />,
            title: 'Длительность',
            desc: 'От 30 минут до 1.5 часов в зависимости от выбранного тарифа'
          }, {
            icon: <MessageCircle className="w-5 h-5" data-id="element-23364" />,
            title: 'Живой диалог',
            desc: 'Возможность задать любые вопросы и получить развёрнутые ответы'
          }, {
            icon: <BookOpen className="w-5 h-5" data-id="element-23365" />,
            title: 'Подготовка',
            desc: 'Астролог заранее изучает вашу карту — время встречи используется эффективно'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl px-5 py-5 border border-slate-100" data-id="element-23366">
                <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-23367">
                  {item.icon}
                </div>
                <div data-id="element-23368">
                  <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-23369">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600" data-id="element-23370">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ОПИСАНИЕ АСТРОЛОГА */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-23371">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23372">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-23373">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-23374">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-23375">
                <span className="text-xs font-bold text-sky-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-23376">
                  Ваш астролог
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3 leading-tight" data-id="element-23377">
                  Елена Астрова
                </h2>
                <p className="text-sky-300 text-sm font-medium mb-4 sm:mb-6" data-id="element-23378">
                  Профессиональный астролог • Опыт 12+ лет
                </p>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-23379">
                  Астролог с многолетним опытом анализа натальных карт,
                  прогнозирования и консультирования. Специализируется на
                  вопросах карьеры, отношений и личностного развития. Провела
                  более 3 000 персональных консультаций.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6 sm:mb-8" data-id="element-23380">
                  {[{
                  label: 'Консультаций',
                  value: '3 000+'
                }, {
                  label: 'Опыт',
                  value: '12 лет'
                }, {
                  label: 'Рейтинг',
                  value: '4.9 ★'
                }, {
                  label: 'Специализация',
                  value: 'Натал, Транзиты'
                }].map((stat, i) => <div key={i} data-id="element-23381">
                      <div className="text-xl font-bold text-white" data-id="element-23382">
                        {stat.value}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-23383">{stat.label}</div>
                    </div>)}
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-23384">
                  {['Натальная карта', 'Транзиты', 'Синастрия', 'Соляр', 'Карьера', 'Отношения'].map((tag, i) => <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-3 py-1.5 rounded-full" data-id="element-23385">
                      {tag}
                    </span>)}
                </div>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-23386">
                <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative" data-id="element-23387">
                  <div className="w-64 h-72 bg-gradient-to-br from-sky-100 to-indigo-100 rounded-2xl shadow-2xl flex flex-col items-center justify-center relative overflow-hidden" data-id="element-23388">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/20" data-id="element-23389" />
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center mb-4 shadow-lg relative z-10" data-id="element-23390">
                      <User className="w-14 h-14 text-white" strokeWidth={1.5} data-id="element-23391" />
                    </div>
                    <div className="w-24 h-3 bg-slate-300 rounded mb-2 relative z-10" data-id="element-23392" />
                    <div className="w-16 h-2 bg-slate-200 rounded mb-3 relative z-10" data-id="element-23393" />
                    <div className="flex gap-0.5 relative z-10" data-id="element-23394">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-23395" />)}
                    </div>
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center shadow-lg" data-id="element-23396">
                    <Award className="w-5 h-5 text-white" data-id="element-23397" />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. ПРЕИМУЩЕСТВА ЛИЧНОЙ КОНСУЛЬТАЦИИ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23398">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23399">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23400">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23401">
              Преимущества личной консультации
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-23402">
            {[{
            icon: <User className="w-6 h-6" data-id="element-23403" />,
            color: 'bg-sky-100 text-sky-600',
            title: 'Индивидуальный подход',
            desc: 'Астролог фокусируется именно на ваших вопросах и жизненной ситуации, а не даёт общие описания.'
          }, {
            icon: <Eye className="w-6 h-6" data-id="element-23404" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Глубокий анализ карты',
            desc: 'Живой разбор позволяет увидеть нюансы и связи, которые невозможно передать в автоматическом отчёте.'
          }, {
            icon: <MessageCircle className="w-6 h-6" data-id="element-23405" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Ответы на личные вопросы',
            desc: 'Вы можете задать любые вопросы и получить развёрнутые ответы с учётом вашей карты.'
          }, {
            icon: <Video className="w-6 h-6" data-id="element-23406" />,
            color: 'bg-emerald-100 text-emerald-600',
            title: 'Живое общение с экспертом',
            desc: 'Диалог с профессионалом даёт более глубокое понимание, чем чтение текста.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100" data-id="element-23407">
                <div className="flex items-start gap-5" data-id="element-23408">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-23409">
                    {item.icon}
                  </div>
                  <div data-id="element-23410">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-23411">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-23412">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 8. ОТЗЫВЫ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23413">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23414">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23415">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23416">
              Отзывы клиентов
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23417">
              Более 3 000 довольных клиентов
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-23418">
            {reviews.map((review, i) => <motion.div key={i} variants={staggerItem} data-id="element-23419">
                <Card padding="lg" className="h-full border-slate-100" data-id="element-23420">
                  <div className="flex items-center gap-3 mb-4" data-id="element-23421">
                    <div className={`w-10 h-10 rounded-full ${review.color} flex items-center justify-center font-bold text-sm`} data-id="element-23422">
                      {review.avatar}
                    </div>
                    <div data-id="element-23423">
                      <div className="text-sm font-bold text-slate-900" data-id="element-23424">
                        {review.name}
                      </div>
                      <Rating value={review.rating} showCount={false} size="sm" data-id="element-23425" />
                    </div>
                  </div>
                  <div className="relative" data-id="element-23426">
                    <Quote className="w-5 h-5 text-slate-200 absolute -top-1 -left-1" data-id="element-23427" />
                    <p className="text-slate-600 text-sm leading-relaxed pl-5" data-id="element-23428">
                      {review.text}
                    </p>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* 9. ДЛЯ КОГО */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23429">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23430">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-23431">
            Для кого подходит консультация
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-23432">
            {[{
            icon: <Eye className="w-5 h-5" data-id="element-23433" />,
            text: 'Людям, которые хотят глубже понять свою натальную карту'
          }, {
            icon: <Compass className="w-5 h-5" data-id="element-23434" />,
            text: 'Тем, кто переживает важный жизненный период'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-23435" />,
            text: 'Людям, которые ищут ответы на важные вопросы'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-23436" />,
            text: 'Тем, кто интересуется астрологией и хочет разобраться в карте'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-23437" />,
            text: 'Тем, кто стоит перед важным выбором или решением'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-23438" />,
            text: 'Людям, которые хотят разобраться в отношениях'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-23439">
                <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center flex-shrink-0" data-id="element-23440">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-23441">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 10. CTA */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-23442">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-23443">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-23444">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-23445">
              <MessageCircle className="w-10 h-10 text-sky-400 mx-auto mb-4 sm:mb-6" data-id="element-23446" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-23447">
              Запишитесь на консультацию с астрологом
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-23448">
              Выберите удобное время и получите персональный астрологический
              разбор с ответами на ваши вопросы.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-23449">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-sky-500/20 bg-sky-500 hover:bg-sky-600" data-id="element-23450">
                Записаться на консультацию
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-23451" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-23452">
              <span className="flex items-center" data-id="element-23453">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-23454" />{' '}
                Конфиденциально
              </span>
              <span className="flex items-center" data-id="element-23455">
                <Video className="w-4 h-4 mr-1.5 text-sky-400" data-id="element-23456" /> Онлайн из
                любой точки
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* 11. ТАРИФЫ */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-23457">
        <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-23458">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23459">
            <div className="text-center mb-8 sm:mb-16" data-id="element-23460">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23461">
                Выберите формат консультации
              </h2>
              <p className="text-base sm:text-lg text-slate-600" data-id="element-23462">
                Три варианта — от экспресс-разбора до расширенной сессии
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-23463">
              {/* Короткая */}
              <Card padding="lg" className="flex flex-col border-slate-200" data-id="element-23464">
                <Badge variant="neutral" className="w-fit mb-6" data-id="element-23465">
                  Экспресс
                </Badge>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-23466">
                  Короткая консультация
                </h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow" data-id="element-23467">
                  Экспресс-разбор одного конкретного вопроса или темы по
                  натальной карте.
                </p>
                <ul className="space-y-2.5 mb-6 sm:mb-8" data-id="element-23468">
                  {['30 минут онлайн', 'Разбор 1 вопроса', 'Предварительный анализ карты', 'Краткие рекомендации'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-23469">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-23470" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-23471">
                  2 990 ₽
                </div>
                <Button variant="outline" fullWidth onClick={() => handleBookConsultation('short')} data-id="element-23472">
                  Записаться
                </Button>
              </Card>

              {/* Стандартная */}
              <Card padding="lg" className="flex flex-col border-sky-200 ring-1 ring-sky-100 shadow-xl shadow-sky-100/50 relative" data-id="element-23473">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 bg-sky-500 text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-23474">
                  Популярный
                </div>
                <Badge variant="pro" className="w-fit mb-6" data-id="element-23475">
                  Стандарт
                </Badge>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-23476">
                  Стандартная консультация
                </h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow" data-id="element-23477">
                  Полный разбор натальной карты с ответами на несколько вопросов
                  и практическими рекомендациями.
                </p>
                <ul className="space-y-2.5 mb-6 sm:mb-8" data-id="element-23478">
                  {['60 минут онлайн', 'Полный разбор натальной карты', 'Ответы на 3-5 вопросов', 'Анализ текущих транзитов', 'Практические рекомендации'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-23479">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-23480" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-23481">
                  4 990 ₽
                </div>
                <Button variant="primary" fullWidth onClick={() => handleBookConsultation('standard')} className="bg-sky-500 hover:bg-sky-600" data-id="element-23482">
                  Записаться
                </Button>
              </Card>

              {/* Расширенная */}
              <Card padding="lg" className="flex flex-col border-slate-200" data-id="element-23483">
                <Badge variant="neutral" className="w-fit mb-6" data-id="element-23484">
                  Расширенная
                </Badge>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-23485">
                  Расширенная консультация
                </h3>
                <p className="text-slate-500 text-sm mb-6 flex-grow" data-id="element-23486">
                  Глубокий разбор натальной карты, транзитов и соляра с
                  подробными рекомендациями.
                </p>
                <ul className="space-y-2.5 mb-6 sm:mb-8" data-id="element-23487">
                  {['90 минут онлайн', 'Полный разбор натальной карты', 'Анализ транзитов и соляра', 'Неограниченные вопросы', 'Запись консультации', 'Письменное резюме после встречи'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-23488">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-23489" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-23490">
                  7 990 ₽
                </div>
                <Button variant="outline" fullWidth onClick={() => handleBookConsultation('extended')} data-id="element-23491">
                  Записаться
                </Button>
              </Card>
            </div>

            <p className="text-center text-sm text-slate-500 mt-8 flex items-center justify-center gap-4" data-id="element-23492">
              <span className="flex items-center" data-id="element-23493">
                <Shield className="w-4 h-4 mr-1 text-emerald-500" data-id="element-23494" /> Безопасная
                оплата
              </span>
              <span className="flex items-center" data-id="element-23495">
                <Calendar className="w-4 h-4 mr-1 text-sky-500" data-id="element-23496" /> Гибкое
                расписание
              </span>
            </p>
          </div>
        </AnimatedSection>
      </div>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23497">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23498">
          <div className="text-center mb-8 sm:mb-12" data-id="element-23499">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23500">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Как проходит онлайн-консультация?',
          content: 'Консультация проводится через видеозвонок (Zoom или Google Meet). Вы получите ссылку на встречу после оплаты. Астролог заранее подготовит анализ вашей карты, поэтому время встречи используется максимально эффективно.'
        }, {
          id: '2',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Для максимально точной консультации желательно знать время рождения. Если точное время неизвестно, астролог может провести процедуру ректификации (восстановление времени по событиям жизни) за дополнительную плату.'
        }, {
          id: '3',
          title: 'Можно ли перенести консультацию?',
          content: 'Да, вы можете перенести консультацию не позднее чем за 24 часа до назначенного времени. Для этого свяжитесь с нами через личный кабинет или по email.'
        }, {
          id: '4',
          title: 'Будет ли запись консультации?',
          content: 'Запись консультации включена в расширенный тариф. Для стандартной и короткой консультации запись доступна за дополнительную плату.'
        }, {
          id: '5',
          title: 'Какие вопросы можно задать?',
          content: 'Любые вопросы, связанные с вашей жизнью: отношения, карьера, финансы, здоровье, переезд, личностное развитие, совместимость с партнёром, выбор времени для важных решений и многое другое.'
        }]} data-id="element-23501" />
        </div>
      </AnimatedSection>
    </div>;
}